<script lang="ts" generics="T extends unknown=string">
  import clsx from 'clsx';
  import { type Snippet } from 'svelte';

  let {
    inputElement = $bindable(),
    suggestions,
    placement,
    renderSuggestion,
    children,
  }: {
    inputElement: HTMLInputElement | undefined;
    suggestions: Array<{ label: T; onClick: () => void }>;
    renderSuggestion?: Snippet<[label: T]>;
    children: Snippet<
      [suggestionSnippet: Snippet<[]>, onKeyDown: (inputEvent: KeyboardEvent) => void]
    >;
    placement?: 'center' | 'left' | 'right';
  } = $props();

  const handleSuggestionEvent = (event: KeyboardEvent) => {
    if (!Array.isArray(suggestions)) {
      return;
    }

    const focusedSuggestion = suggestionElements.findIndex((x) => x === activeElement);
    if (event.code === 'Escape' && inputElement && activeElement !== inputElement) {
      inputElement.focus();
    }
    if (event.code === 'Enter') {
      const currentTarget = event.currentTarget as HTMLElement;
      currentTarget.click();
      currentTarget.blur();
      return;
    }
    const term = event.code === 'ArrowUp' ? -1 : event.code === 'ArrowDown' ? 1 : 0;

    if (term === 0) {
      return;
    }

    event.preventDefault();

    const foundElem =
      (suggestionElements.at(focusedSuggestion + term) as HTMLElement | undefined) ??
      (suggestionElements.at(0) as HTMLElement | undefined);
    foundElem?.focus();
  };

  const placementClass = $derived.by(() => {
    if (!placement) {
      return 'left-0 right-0';
    }

    switch (placement) {
      case 'center':
        return 'left-1/2 -translate-x-1/2';
      case 'left':
        return 'left-0';
      case 'right':
        return 'right-0';
    }
  });

  let suggestionParent = $state<HTMLElement | null>(null);

  const suggestionElements = $derived([...(suggestionParent?.children ?? [])]);

  let activeElement = $state<HTMLElement | null>(null);

  const showSuggestions = $derived(
    (activeElement && suggestionElements.includes(activeElement as Element)) ||
      activeElement === inputElement,
  );

  const setActiveElement = (event: Event) => {
    if (event.currentTarget instanceof HTMLElement) {
      activeElement = event.currentTarget;
    }
  };

  const handleClick = (e: MouseEvent) => {
    if (inputElement && !inputElement.contains(e.target as Node)) {
      activeElement = null;
    }
    return;
  };

  $effect(() => {
    inputElement?.addEventListener('focusin', setActiveElement);
    document.addEventListener('click', handleClick);
    return () => {
      inputElement?.removeEventListener('focusin', setActiveElement);
      document.addEventListener('click', handleClick);
    };
  });
</script>

{#snippet defaultRenderSuggestion(label: T)}
  <div class="w-full">{label}</div>
{/snippet}

{#snippet suggestionSnippet()}
  {#if Array.isArray(suggestions) && suggestions.length > 0 && showSuggestions}
    <div
      bind:this={suggestionParent}
      class={clsx(
        'absolute top-full z-20 flex w-max min-w-full flex-col rounded-sm border border-gray-300 bg-st-white text-sm shadow-2xl',
        placementClass,
      )}
    >
      {#each suggestions as suggestion}
        <button
          tabindex={-1}
          onfocus={setActiveElement}
          type="button"
          onkeydown={handleSuggestionEvent}
          class={clsx(
            'w-full border-b border-st-gray-300 p-0 text-left last:border-0 focus:bg-st-blue-200',
          )}
          onclick={(e) => {
            suggestion.onClick();
            e.currentTarget.blur();
          }}
          onmousedown={() => {
            suggestion.onClick();
            activeElement?.blur();
          }}
        >
          {@render (renderSuggestion ?? defaultRenderSuggestion)(suggestion.label)}
        </button>
      {/each}
    </div>
  {/if}
{/snippet}

{@render children(suggestionSnippet, handleSuggestionEvent)}
